import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { MechanicsService } from "./mechanics.service"
import { UtilsService } from "./utils.service"

import { User } from '../interfaces/user.interface';
import { Company } from '../interfaces/company.interface';
import { Agency } from '../interfaces/agency.interface';
import { deepClone, defaults } from '../utils';
import { lastValueFrom } from 'rxjs';

import { environment } from "../../environments/environment"

@Injectable()
export class UserService {

	rolesMap = ["admin", "accreditor", "company admin", "company editor", "basic", "guest", "Agency admin", "Agency staff"]

	public user: User // Initialized in constructor

	public messageToCompany: string = ""

	public secondsBeforeLogout: number
	public intervalLogout = null
	public intervalKeepalive = null

	// Step 1: credentials,
	// Step 2: verifying the code sent by mail
	public loginStep: "credentials" | "emailCodeCheck" = "credentials";

	constructor(public http: HttpClient,
		public ms: MechanicsService,
		public utils: UtilsService,
		public router: Router) {

		const l = `us constructor - `

		// console.log(`user.service constructor - user = `, this.user)

		this.resetUserObject()
		this.loadUserFromSS()


		const autoLoginCredentials = localStorage.getItem("autoLoginCredentials");

		if (autoLoginCredentials) {
			// console.log(`${l}Logging in automatically`);
			this.login(JSON.parse(autoLoginCredentials))
		}
	}

	can(what): boolean { // Permission checker

		const l = `us.can() - `

		/* ROLES (type = number)
			0 = admin - God almighty
			1 = accreditor - can give privileges to company editors and company admins, but not use the manager to edit patents or families
			2 = company admin - can give privileges to basic accounts to become company editors or admins
			3 = company editor - can use the manager to edit patents or families
			// DEPRECATED 4 = Basic - can just browse and contact companies. --> Deprecated because simple users can't create an account anymore
			5 = guest, can see everything like admins but can't save or update anything
			6 = Agency admin
			7 = Agency staff
		*/

		let role = this.user["role"]
		// if ([0, 5].includes(role)) return true; --> Yeah no, I don't want to show the Contact e-mails tabs to admins,that's only for companies and me for dev

		if (!this.isLoggedIn) return false;

		switch (what) {
			case "jerOnly":
				return this.user.email === "jeremy.thille@wipo.int"
			case "manager":
				return [0, 1, 2, 3, 5].includes(role) // Agency accounts don't need a Manager at all, they have no business in there
			case "tabInns":
			case "tabFamilies":
				return [0, 2, 3, 5].includes(role)
			case "tabEmails":
				return [1, 2].includes(role) || this.ms.isLocalHost
			case "tabAccounts":
				return [0, 1, 2, 5, 6].includes(role)
			case "contactCompanies":
				return [0, 5, 6, 7].includes(role)
			case "tabCounts":
				return [0, 2, 3, 4, 5].includes(role)
			case "adminOrGuest":
			case "tabAgencies":
			case "create user":
			case "create company":
			case "create agency":
			case "manage accounts":
			case "seeOwner":
			case "approve pscope links":
			case "see virtuals by default":
			case "create virtual patents": // Companies create real patents, we create virtual patents
				return [0, 1, 5].includes(role)
			case "tabStats":
			case "tabAdmintools":
			case "admin":
			case "admins":
				return role === 0; // Strictly only admins. Guests can't have access to this
			default:
				return false
		}
	}

	async call(route: string, payload = {}): Promise<any> {

		const l = `US.call() - `

		this.ms.isLoading = true

		let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + (this.user.token && this.user.token.value || "") })

		// console.log(`\nuserService call ${route}`)

		let res: any;

		try {

			const url = `${environment.apiUrl}${window['urlPrefix']}${route}`

			// console.log(`Calling url = ${url}`)

			res = await lastValueFrom(this.http.post(url, payload, { headers, observe: "response" }))

			this.ms.isLoading = false
			this.checkAndUpdateToken(res)
			return res.body

		} catch (err) {

			this.ms.isLoading = false

			console.error("userService.call().catch(err) = ", err)

			if (route === "/api/login") {

				let errCode = err.error?.error; // "not approved"

				console.log(`Login catch - errCode = `, errCode);

				switch (errCode) {
					case "incorrect credentials":
						this.utils.toast({ severity: 'error', title: 'Login failed', detail: 'Incorrect credentials' }, { timeOut: 2500 })
						break;
					case "disabled":
						this.utils.toast({ severity: 'error', title: 'Account disabled', detail: 'This account has been disabled by an administrator.' }, { timeOut: 4000 })
						break;
					case "not verified":
						this.utils.toast({ severity: 'error', title: 'Not verified', detail: 'This account has not been verified. Please click the link in the activation email we sent you.' }, { timeOut: 4000 })
						break;
					case "not approved":
						this.utils.toast({ severity: 'error', title: 'Not yet approved', detail: 'This account has not been approved by an administrator.' }, { timeOut: 4000 })
						break;
					case "invalid code":
						this.utils.toast({ severity: 'error', title: 'Invalid code', detail: 'The code is invalid or expired.' }, { timeOut: 4000 })
						break;
					case "No emailCode inserted":
						this.utils.toast({ severity: 'error', title: 'Technical error', detail: 'Please try logging in again' }, { timeOut: 4000 })
						break;
					default:
						// Show a generic error for any unhandled login errors (like 404)
						this.utils.toast({ severity: 'error', title: 'Login failed', detail: err.statusText || err.message || 'Unable to connect to server' }, { timeOut: 3000 })
						break;
				}

				return
			}


			if (err.status === 401 && this.user.role != 5) {
				this.utils.toast({ severity: 'error', title: `Expired authentication`, detail: err.message || err.error || JSON.stringify(err) }, { timeOut: 3000 })
				this.logout();
				return
			}

			console.warn("CALL error: ", err)

			let errMsg = `Error calling ${route}`
			if (err.message) errMsg += ` - (${err.message})`

			this.utils.toast({ severity: "error", detail: errMsg }, { timeOut: 2000 })
		}
	}

	checkAndUpdateToken(rawRes) {

		const l = `us.checkAndUpdateToken() - `

		// console.log(`${l}rawRes = `, rawRes)

		if (!rawRes.headers) return console.warn("checkAndUpdateToken - no rawRes.headers! rawRes = ", rawRes);

		if (rawRes.body?.freshToken) this.user.token = rawRes.body.freshToken;

		// console.log(`User token =  `, this.user.token)
	}

	computeUserCustomKeys(user: User): User {
		const l = `computeUserCustomKeys() - `

		user.roleHuman = this.rolesMap[user.role]
		user.createdHuman = this.utils.dateToHuman(user.created, true)

		try {
			user.lastLoginHuman = this.utils.dateToHuman(new Date(user.lastLogin).toISOString(), true)
		} catch (err) {
			//console.warn(`${l}Could not compute lastLoginHuman, it's probably null, that's fine`, err)
		}

		user.approvedHuman = user.approval.status === "approved" ? `Yes, by ${user.approval.by} on ${this.utils.dateToHuman(user.approval.date, true)}` : "No"
		user.emailDomain = user.email.split("@").pop();

		user.preferences = user.preferences || deepClone(defaults.user.preferences);

		return user
	}

	async login(credentials?: Object) { // credentials is an override object, for DEV one-click login

		const l = `us.login() - `

		this.ms.isLoading = true

		// console.log(`userService.login()`)

		const body = await this.call(`/api/login`, {
			email: credentials ? credentials["email"] : this.user.email,
			password: credentials ? credentials["password"] : this.user.password,
			emailCode: credentials ? credentials["emailCode"] : this.user.emailCode,
		})

		if (!body) { // probably fell in the call.catch()
			return
		}

		let result = body;

		console.log(`${l}Got result = `, result);

		// Check for error response from backend
		if (result.error) {
			console.error(`${l}Login error detected:`, result.error);
			this.ms.isLoading = false;
			switch (result.error) {
				case "incorrect credentials":
					this.utils.toast({ severity: 'error', title: 'Login failed', detail: 'Incorrect credentials' }, { timeOut: 2500 });
					break;
				case "disabled":
					this.utils.toast({ severity: 'error', title: 'Account disabled', detail: 'This account has been disabled by an administrator.' }, { timeOut: 4000 });
					break;
				case "not verified":
					this.utils.toast({ severity: 'error', title: 'Not verified', detail: 'This account has not been verified. Please click the link in the activation email we sent you.' }, { timeOut: 4000 });
					break;
				case "not approved":
					this.utils.toast({ severity: 'error', title: 'Not yet approved', detail: 'This account has not been approved by an administrator.' }, { timeOut: 4000 });
					break;
				case "invalid code":
					this.utils.toast({ severity: 'error', title: 'Invalid code', detail: 'The code is invalid or expired.' }, { timeOut: 4000 });
					break;
				default:
					this.utils.toast({ severity: 'error', title: 'Login failed', detail: result.error }, { timeOut: 2500 });
			}
			return;
		}

		if (result.message === "emailCode sent") {
			this.loginStep = "emailCodeCheck";
			return;
		}

		// HERE: the email validaion code has been approved, proceeding

		this.loginStep = "credentials";

		this.user["password"] = "";
		this.user["emailCode"] = "";
		this.user = Object.assign(this.user, result);

		this.saveUserToSS();

		this.user = this.computeUserCustomKeys(this.user);

		this.utils.initDateFormatter(this.user.preferences.dateFormat);

		/*
		if (this.ms.disableLogout) {
			// console.log(`\nActivating Keepalive.`)
			this.intervalKeepalive = setInterval(() => this.keepAlive(), 3 * 60 * 1000)
		}
		*/

		window["user"] = this.user // exposing things, not only for dev, but I need the user object in utils.ts, and that's pretty much the only way without injection dependency
		this.utils.toast({ severity: 'success', detail: 'Login successful' }, { timeOut: 700 })
		this.ms.isLoading = false
		this.ms.closeCurrentModal(l)

		await this.loadSettings()

		//Argh, not for regular users with an account!
		if (this.user.role < 6) {
			this.ms.navigate("manager")
		}

	}

	resetUserObject() {
		this.user = deepClone(defaults.user)
		if (this.ms.isLocalHost) window["user"] = this.user // DEV, exposing things

		// console.log(`user service resetUserObject() - user.password = `, this.user.password)
	}


	async logout(): Promise<void> {

		const l = `us.logout() - `

		console.log(`${l}Logging out`);

		if (!this.user.token) {
			console.log(`Logout() - no token for some reason. Redirecting to home page`)
			this.resetUserObject();
			sessionStorage.removeItem("PATuser");
			this.router.navigate(['/'])
			return
		}

		this.ms.isLoading = true
		// console.log(`\nLogging out - this.user.token.value = `, this.user.token.value)

		let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + this.user.token.value })

		try {
			await lastValueFrom(this.http.post(`${environment.apiUrl}${window['urlPrefix']}/api/logout`, {}, { headers }))
		} catch (err) {
			console.error(err);
			this.utils.toast({ severity: 'success', detail: 'Logout successful' }, { timeOut: 500 })
			clearInterval(this.intervalLogout)
			clearInterval(this.intervalKeepalive)
		} finally {
			this.resetUserObject()
			sessionStorage.removeItem("PATuser")
			await this.router.navigate(['/'])
			this.ms.isLoading = false
		}
	}

	get isLoggedIn(): boolean {
		return this.user["displayedName"]?.length > 0
	}


	saveUser(user?: User, update?: any) {

		const l = `saveUser() - `

		user = user || this.user
		update = update || this.user

		// console.log(`${l} update = `, update)

		delete update["password"]; // Otherwise the password is overwritten as '' in the DB!

		this.call(`/api/save`, {
			collection: "User",
			target: user,
			update: update || this.user
		})
			.then((body: User) => {
				this.saveUserToSS(body)
				this.utils.toast({ severity: 'success', detail: `User ${user.email} updated` })
			})
			.catch(err => {
				console.error(err);
				this.utils.toast({ severity: 'error', title: `Failed to save user ${user.email}`, detail: 'Something went wrong... Not sure what' }, { timeOut: 2500 })
			})
	}

	saveUserToSS(user?: User) {
		sessionStorage.setItem("PATuser", JSON.stringify(user || this.user))
	}

	loadUserFromSS() {

		const l = `loadUserFromSS - `

		const SSuserString = sessionStorage.getItem("PATuser")

		if (!SSuserString) return;

		try {
			this.user = JSON.parse(SSuserString);
			window["user"] = this.user;
			this.utils.initDateFormatter(this.user.preferences.dateFormat)

			// console.log(`${l}Have loaded profile from session storage: `, this.user);
		} catch (error) {
			 console.warn(`${l}Could not parse SSuserString`)
		}
	}

	async loadSettings(): Promise<void> {
		try {
			const url = `${environment.apiUrl}${window['urlPrefix'] || ''}/api/getSettings`
			const result: any = await lastValueFrom(this.http.post(url, {}))
			this.ms.isFrozen = result?.isFrozen ?? false
		} catch (err) {
			console.warn(`loadSettings() - could not fetch settings:`, err)
		}
	}

	/*
	resetIntervalLogout() {
		this.secondsBeforeLogout = 300

		// console.log(`\nresetIntervalLogout()`)


		if (this.ms.disableLogout) {
			this.intervalLogout = null
		} else {
			this.intervalLogout = this.intervalLogout || setInterval(() => {
				this.secondsBeforeLogout--
				if (this.secondsBeforeLogout <= 0) this.logout()
			}, 1000)
		}
	}

	keepAlive() {

		// console.info(new Date() + ` - Keeping alive`)

		this.call(`/api/keepalive`, { email: this.user.email })
		.then(body => this.saveUserToLs(body))
		.catch(err => console.error(err))


	}
	*/

	userCompany(): Company {
		// Forces return the user's company as a Company, otherwise the compiler refuses to compile the templates because it can also be a string.
		// So, this is used to force the Company type in templates
		return <Company>(this.user._company || Object.create(defaults.company))
	}

	userAgency(): Agency {
		return <Agency>(this.user._company || Object.create(defaults.agency))
	}
}

